export const OPERATIONAL_SCHEDULE_DAYS = [
	{ key: 'MONDAY', label: 'Monday' },
	{ key: 'TUESDAY', label: 'Tuesday' },
	{ key: 'WEDNESDAY', label: 'Wednesday' },
	{ key: 'THURSDAY', label: 'Thursday' },
	{ key: 'FRIDAY', label: 'Friday' },
	{ key: 'SATURDAY', label: 'Saturday' },
	{ key: 'SUNDAY', label: 'Sunday' },
];

const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const TIMEZONE_PATTERN = /^(?:UTC|[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z][A-Za-z0-9_+-]*)+)$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const browserTimezone = () => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
	} catch (error) {
		return 'UTC';
	}
};

export const DEFAULT_OPERATIONAL_TIMEZONE = browserTimezone();

const currentDateInTimezone = (timezone, referenceDate = new Date()) => {
	try {
		const parts = new Intl.DateTimeFormat('en-CA', {
			timeZone: timezone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		}).formatToParts(referenceDate);
		const values = parts.reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
		return `${values.year}-${values.month}-${values.day}`;
	} catch (error) {
		const year = referenceDate.getFullYear();
		const month = String(referenceDate.getMonth() + 1).padStart(2, '0');
		const day = String(referenceDate.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}
};

export const getOperationalScheduleFirstAllowedDate = (timezone) => {
	const [year, month, day] = currentDateInTimezone(timezone || 'UTC').split('-').map(Number);
	return new Date(year, month - 1, day);
};

export const createOperationalSchedule = () => ({
	timezone: DEFAULT_OPERATIONAL_TIMEZONE,
	effectiveFromLocal: currentDateInTimezone(DEFAULT_OPERATIONAL_TIMEZONE),
	effectiveUntilLocal: null,
	runsIndefinitely: true,
	windows: [],
});

const isKnownDay = (dayOfWeek) => OPERATIONAL_SCHEDULE_DAYS.some((day) => day.key === dayOfWeek);

const normalizeWindow = (window) => ({
	dayOfWeek: window.dayOfWeek,
	opensAtLocal: window.opensAtLocal || '',
	closesAtLocal: window.closesAtLocal || '',
	endsNextDay: Boolean(window.endsNextDay),
});

const sortWindows = (windows) =>
	[...windows].sort(
		(left, right) =>
			OPERATIONAL_SCHEDULE_DAYS.findIndex((day) => day.key === left.dayOfWeek) -
			OPERATIONAL_SCHEDULE_DAYS.findIndex((day) => day.key === right.dayOfWeek),
	);

export const normalizeOperationalSchedule = (schedule) => {
	const timezone = schedule?.timezone?.trim() || DEFAULT_OPERATIONAL_TIMEZONE;
	const today = currentDateInTimezone(timezone);
	const configuredEffectiveFromLocal = DATE_PATTERN.test(schedule?.effectiveFromLocal || '')
		? schedule.effectiveFromLocal
		: today;
	const configuredEffectiveUntilLocal = DATE_PATTERN.test(schedule?.effectiveUntilLocal || '')
		? schedule.effectiveUntilLocal
		: null;
	const runsIndefinitely = typeof schedule?.runsIndefinitely === 'boolean'
		? schedule.runsIndefinitely
		: !configuredEffectiveUntilLocal;
	const validWindows = Array.isArray(schedule?.windows)
		? schedule.windows.filter((window) => isKnownDay(window?.dayOfWeek)).map(normalizeWindow)
		: [];

	return {
		timezone,
		effectiveFromLocal: configuredEffectiveFromLocal,
		effectiveUntilLocal: configuredEffectiveUntilLocal,
		runsIndefinitely,
		windows: sortWindows(validWindows),
	};
};

export const copyOperationalSchedule = (schedule) => normalizeOperationalSchedule(schedule);

export const operationalScheduleEquals = (left, right) =>
	JSON.stringify(normalizeOperationalSchedule(left)) === JSON.stringify(normalizeOperationalSchedule(right));

export const serializeOperationalSchedule = (schedule) => {
	const normalized = normalizeOperationalSchedule(schedule);
	return {
		timezone: normalized.timezone.trim(),
		effectiveFromLocal: normalized.effectiveFromLocal,
		effectiveUntilLocal: normalized.runsIndefinitely ? null : normalized.effectiveUntilLocal,
		windows: normalized.windows.map((window) => ({ ...window })),
	};
};

export const validateOperationalSchedule = (schedule) => {
	const normalized = normalizeOperationalSchedule(schedule);
	const errors = {};
	const configuredDays = new Set();

	if (!TIMEZONE_PATTERN.test(normalized.timezone)) {
		errors.timezone = 'Enter an IANA timezone, for example Europe/Brussels.';
	}
	if (!DATE_PATTERN.test(normalized.effectiveFromLocal)) {
		errors.effectiveDates = 'Select the first date for the opening hours.';
	} else if (!normalized.runsIndefinitely && !DATE_PATTERN.test(normalized.effectiveUntilLocal || '')) {
		errors.effectiveDates = 'Select an end date or turn on Runs indefinitely.';
	} else if (normalized.effectiveUntilLocal && normalized.effectiveUntilLocal < normalized.effectiveFromLocal) {
		errors.effectiveDates = 'The end date must be on or after the start date.';
	}

	normalized.windows.forEach((window) => {
		const dayLabel = OPERATIONAL_SCHEDULE_DAYS.find((day) => day.key === window.dayOfWeek)?.label || window.dayOfWeek;
		if (configuredDays.has(window.dayOfWeek)) {
			errors[window.dayOfWeek] = `${dayLabel} can only have one operational window.`;
		}
		configuredDays.add(window.dayOfWeek);

		if (!TIME_PATTERN.test(window.opensAtLocal) || !TIME_PATTERN.test(window.closesAtLocal)) {
			errors[window.dayOfWeek] = `${dayLabel} needs an opening and closing time.`;
		} else if (window.opensAtLocal === window.closesAtLocal) {
			errors[window.dayOfWeek] = `${dayLabel} opening and closing times must differ.`;
		} else if (!window.endsNextDay && window.closesAtLocal <= window.opensAtLocal) {
			errors[window.dayOfWeek] = `${dayLabel} closes before it opens. Mark it as ending the next day for overnight hours.`;
		}
	});

	return {
		isValid: Object.keys(errors).length === 0,
		errors,
		firstMessage: Object.values(errors)[0] || '',
	};
};

export const getIanaTimezoneOptions = (selectedTimezone) => {
	const fallbackTimezones = ['UTC', 'Europe/Brussels', 'Europe/London', 'Europe/Paris', 'Europe/Amsterdam', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney'];
	const timezones =
		typeof Intl.supportedValuesOf === 'function' ? [...Intl.supportedValuesOf('timeZone'), 'UTC'] : fallbackTimezones;

	return [...new Set([...timezones, selectedTimezone].filter(Boolean))]
		.sort()
		.map((timezone) => ({ label: timezone, value: timezone }));
};
